import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WidgetSettings } from '../types/quiz';

const QuizLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<WidgetSettings>({
    school_name: 'Armenta Learning Academy',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981'
  });

  // Fetch widget settings for branding
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/widget/config`);
        if (response.ok) {
          const data = await response.json();
          setSettings({
            school_name: data.school_name || 'Armenta Learning Academy',
            primary_color: data.primary_color || '#4F46E5',
            secondary_color: data.secondary_color || '#7C3AED',
            accent_color: data.accent_color || '#10B981',
            logo_url: data.logo_url
          });
        }
      } catch (error) {
        console.error('Failed to fetch widget settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Load Google Analytics (production only)
  useEffect(() => {
    // Only load GA in production (not on localhost)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('🔧 Development mode - Google Analytics disabled');
      return;
    }

    // Inject GA script
    const script1 = document.createElement('script');
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-42R5PVVB4Z';
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-42R5PVVB4Z');
    `;
    document.head.appendChild(script2);

    console.log('📊 Google Analytics loaded for quiz landing page');

    // Cleanup function to remove scripts when component unmounts
    return () => {
      if (document.head.contains(script1)) document.head.removeChild(script1);
      if (document.head.contains(script2)) document.head.removeChild(script2);
    };
  }, []);

  const handleStartQuiz = () => {
    // Track quiz start event in Google Analytics
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'quiz_started', {
          event_category: 'Quiz',
          event_label: 'Quiz Started from Landing Page'
        });
        console.log('📊 GA Event: quiz_started');
      }
    }
    navigate('/quiz/assessment');
  };

  const primaryColor = settings.primary_color || '#4F46E5';
  const accentColor = settings.accent_color || '#10B981';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-6 py-16 md:py-24">
        <div className="max-w-2xl mx-auto">
          {/* Logo/School Name */}
          {settings.logo_url ? (
            <div className="flex justify-center mb-12">
              <img src={settings.logo_url} alt={settings.school_name} className="h-16 md:h-20" />
            </div>
          ) : (
            <h2 className="text-center text-2xl md:text-3xl font-bold mb-12" style={{ color: primaryColor }}>
              {settings.school_name}
            </h2>
          )}

          {/* Main Headline - Emotional, Problem-First */}
          <div className="text-center mb-10 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-5 md:mb-6 leading-tight px-2">
              Your child isn't broken.
              <br />
              <span className="text-gray-700">They just learn differently.</span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-4 px-2">
              Discover their unique learning profile in{' '}
              <span className="font-bold" style={{ color: accentColor }}>3 minutes</span>.
            </p>
          </div>

          {/* Simple Benefits List - No Cards */}
          <div className="mb-10 md:mb-12 bg-white rounded-lg p-6 sm:p-8 md:p-10 shadow-sm">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">
              What you'll discover:
            </h3>

            <div className="space-y-4 md:space-y-5">
              <div className="flex items-start gap-3 md:gap-4">
                <svg className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" style={{ color: accentColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-base md:text-lg text-gray-800">
                  <strong>What your child actually needs to succeed</strong> — not what schools think they need
                </p>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <svg className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" style={{ color: accentColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-base md:text-lg text-gray-800">
                  <strong>The best way they learn</strong> — their unique brain, not a one-size-fits-all approach
                </p>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <svg className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" style={{ color: accentColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-base md:text-lg text-gray-800">
                  <strong>Concrete strategies you can use THIS WEEK</strong> — real solutions, not theory
                </p>
              </div>
            </div>
          </div>

          {/* Direct CTA */}
          <div className="text-center mb-10 md:mb-12">
            <button
              onClick={handleStartQuiz}
              className="px-8 sm:px-12 py-4 sm:py-5 rounded-xl text-white font-bold text-lg sm:text-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 w-full md:w-auto min-h-[56px]"
              style={{
                backgroundColor: primaryColor
              }}
            >
              Take the 3-Minute Quiz →
            </button>
            <p className="mt-4 text-sm sm:text-base text-gray-500 px-4">
              100% free • No signup required • Get instant results
            </p>
          </div>

          {/* Simple Trust Bar */}
          <div className="text-center pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Trusted by parents who were tired of guessing what their child needs
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizLandingPage;
